<template>
  <div
    :class="[{ 'custom-input': true }, { 'has-error': isError || error }]"
    class="input-wrapper"
  >
    <div :class="parentClass">
      <div class="relative">
        <span class="prepend-text">{{ prepend }}</span>
        <span class="append-text">{{ append }}</span>
        <div
          :class="[
            rootClasses,
            childClass,
            { 'has-append': append },
            { 'has-prepend': prepend }
          ]"
          class="control"
        >
          <input
            v-if="type !== 'textarea'"
            ref="input"
            :class="[
              inputClasses,
              { 'is-danger': (isError || error) && !$attrs.disabled }
            ]"
            :type="newType"
            :autocomplete="newAutocomplete"
            :placeholder="!prepend && placeholder ? placeholder : ''"
            :maxlength="maxlength"
            :value="newValue"
            v-bind="$attrs"
            class="input"
            @input="onInput"
            @blur="onBlur"
            @focus="onFocus"
          />

          <textarea
            v-else
            ref="textarea"
            :class="[
              inputClasses,
              { 'is-danger': (isError || error) && $attrs && !$attrs.disabled }
            ]"
            :maxlength="maxlength"
            :value="newValue"
            :placeholder="placeholder ? placeholder : ''"
            v-bind="$attrs"
            class="textarea"
            @input="onInput"
            @blur="onBlur"
            @focus="onFocus"
          />

          <b-icon
            v-if="icon"
            :icon="icon"
            :pack="iconPack"
            :size="iconSize"
            class="is-left"
          />

          <b-icon
            v-if="!loading && (passwordReveal || statusType)"
            :class="{ 'is-clickable': passwordReveal }"
            :icon="passwordReveal ? passwordVisibleIcon : statusTypeIcon"
            :pack="iconPack"
            :size="iconSize"
            :type="!passwordReveal ? statusType : 'is-primary'"
            class="is-right"
            both
            @click.native="togglePasswordVisibility"
          />

          <small
            v-if="maxlength && hasCounter && type !== 'number'"
            :class="{ 'is-invisible': !isFocused }"
            class="help counter"
          >
            {{ valueLength }} / {{ maxlength }}
          </small>
        </div>
      </div>
    </div>
    <div
      v-for="(validError, index) in validateErrors"
      v-if="validError.valid && $attrs && !$attrs.disabled"
      :key="index"
    >
      <p class="help is-danger error-text">{{ validError.message }}</p>
    </div>
    <p
      v-if="error && $attrs && !$attrs.disabled"
      class="help is-danger error-text"
    >
      {{ error }}
    </p>
  </div>
</template>
<style src="./b-custom-input.scss" lang="scss">
</style>
<script>
import FormElementMixin from 'buefy/src/utils/FormElementMixin';

export default {
  name: 'BCustomInput',
  mixins: [FormElementMixin],
  inheritAttrs: false,
  props: {
    // eslint-disable-next-line
    value: [Number, String],
    type: {
      type: String,
      default: 'text'
    },
    mask: {
      type: String,
      default: ''
    },
    isNumber: {
      type: Boolean,
      default: () => false
    },
    isInteger: {
      type: Boolean,
      default: () => false
    },
    isIntegerCode: {
      type: Boolean,
      default: () => false
    },
    isHashCode: {
      type: Boolean,
      default: () => false
    },
    isError: {
      type: Boolean,
      default: () => false
    },
    validateErrors: {
      type: Array,
      default: () => []
    },
    isTouch: {
      type: Function,
      default: () => {}
    },
    validateFunction: {
      type: Function,
      default: () => {}
    },
    passwordReveal: {
      type: Boolean,
      default: () => false
    },
    hasCounter: {
      type: Boolean,
      default: () => true
    },
    prepend: {
      type: String,
      default: ''
    },
    append: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    childClass: {
      type: String,
      default: ''
    },
    parentClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      newValue: this.mask ? this.mask.replace(/[0-9]/g, '_') : this.value,
      newType: this.type,
      newAutocomplete: this.autocomplete || 'on',
      isPasswordVisible: false,
      error: '',
      // eslint-disable-next-line
      _elementRef: this.type === 'textarea' ? 'textarea' : 'input'
    };
  },
  computed: {
    rootClasses() {
      return [
        this.iconPosition,
        this.size,
        {
          'is-expanded': this.expanded,
          'is-loading': this.loading,
          'is-clearfix': !this.hasMessage
        }
      ];
    },
    inputClasses() {
      return [this.statusType, this.size, { 'is-rounded': this.rounded }];
    },
    hasIconRight() {
      return this.passwordReveal || this.loading || this.statusType;
    },

    /**
     * Position of the icon or if it's both sides.
     */
    iconPosition() {
      if (this.icon && this.hasIconRight) {
        return 'has-icons-left has-icons-right';
      } else if (!this.icon && this.hasIconRight) {
        return 'has-icons-right';
      } else if (this.icon) {
        return 'has-icons-left';
      }
    },

    /**
     * Icon name (MDI) based on the type.
     */
    statusTypeIcon() {
      switch (this.statusType) {
        case 'is-success':
          return 'check';
        case 'is-danger':
          return 'alert-circle';
        case 'is-info':
          return 'information';
        case 'is-warning':
          return 'alert';
      }
    },

    /**
     * Check if have any message prop from parent if it's a Field.
     */
    hasMessage() {
      // eslint-disable-next-line
      return !!this.statusMessage;
    },

    /**
     * Current password-reveal icon name.
     */
    passwordVisibleIcon() {
      return !this.isPasswordVisible ? 'eye' : 'eye-off';
    },
    /**
     * Get value length
     */
    valueLength() {
      if (typeof this.newValue === 'string') {
        return this.newValue.length;
      } else if (typeof this.newValue === 'number') {
        return this.newValue.toString().length;
      }
      return 0;
    }
  },
  watch: {
    /**
     * When v-model is changed:
     *   1. Set internal value.
     *   2. If it's invalid, validate again.
     */
    value(value) {
      this.newValue = value;
    },

    /**
     * Update user's v-model and validate again whenever
     * internal value is changed.
     */
    newValue(value) {
      this.$emit('input', value);
      !this.isValid && this.checkHtml5Validity();
    }
  },
  methods: {
    /**
     * Toggle the visibility of a password-reveal input
     * by changing the type and focus the input right away.
     */
    togglePasswordVisibility() {
      this.isPasswordVisible = !this.isPasswordVisible;
      this.newType = this.isPasswordVisible ? 'text' : 'password';

      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },

    /**
     * Input's 'input' event listener, 'nextTick' is used to prevent event firing
     * before ui update, helps when using masks (Cleavejs and potentially others).
     */
    onInput(event) {
      if (this.type === 'text' && this.isHashCode) {
        event.target.value = event.target.value.replace(/[^A-Za-z0-9]+/g, '');
      }

      if (this.type === 'text' && (this.isNumber || this.isInteger || this.isIntegerCode)) {
        let value = event.target.value.replace(/[^0-9\\.]+/g, '');

        if (!this.isIntegerCode && value.charAt(0) === '0' && value.length > 1 && value.charAt(1) !== '.')
          value = value.replace('0', '');

        event.target.value = value;
      }

      if (this.isInteger || this.isIntegerCode) event.target.value = event.target.value.replace(/\./g, '');

      if (this.mask) {
        const maskPhrases = this.mask.split('-');
        let valueInput = event.target.value.replace(/\D+/g, '');
        const valueInputChars = valueInput.split('');
        const newValues = [];
        let numberIndex = 0;
        let pointerPosition = 0;
        maskPhrases.forEach((maskPhrase, index) => {
          const maskDefault = maskPhrase.replace(/[^-]/g, '_');
          const maskChars = maskDefault.split('');
          for (let i = 0; i < maskChars.length; i++) {
            if (valueInputChars[numberIndex]) {
              maskChars[i] = valueInputChars[numberIndex];
              numberIndex++;
              pointerPosition++;
            }
          }
          newValues.push(maskChars.join(''));
          if (valueInputChars[numberIndex]) pointerPosition++;
        });
        event.target.value = newValues.join('-');
        event.target.setSelectionRange(pointerPosition, pointerPosition);
      }

      this.$nextTick(() => {
        this.newValue = event.target.value;
      });
    },
    onBlur($event) {
      if (
        (this.type === 'text' || this.type === 'textarea') &&
        this.newValue &&
        !this.isNumber &&
        !this.isInteger &&
        !this.isIntegerCode
      )
        this.newValue = this.newValue.trim();

      const valid = this.validateFunction(this.newValue);
      if (valid && 'messageId' in valid && valid.messageId) this.error = this.$t(valid.messageId);
      else this.error = null;

      if (this.$refs.input && this.$refs.input.disabled) this.error = null;

      this.isFocused = false;
      this.$emit('blur', $event);
      // this.checkHtml5Validity();
    }
  }
};
</script>
